import { AsyncPipe } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    signal,
} from '@angular/core';
import { MatSelectModule } from '@angular/material/select';

import {
    FormArray,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { debounceTime, filter, Observable, of, Subject, takeUntil } from 'rxjs';
import { NavigationText } from '../../../../../../core/constants/navigation-text';
import { FormGroupType } from '../../../../../../core/models/form-group-type';
import { GridHelper } from '../../../../../../core/models/grid/grid-helper';
import { RegExPattern } from '../../../../../../core/models/regexp-pattern/regex-pattern';
import { ActionCode } from '../../../../../../core/models/resources/action-code';
import { DialogTooltipComponent } from '../../../../../../shared/components/tooltips/dialog-tooltip/dialog-tooltip.component';
import { HoverTooltipComponent } from '../../../../../../shared/components/tooltips/hover-tooltip/hover-tooltip.component';
import { ApplicationFormControlManagerService } from '../../../../shared/services/application-form-control-manager/application-form-control-manager.service';
import { ScheduleDFormModel } from '../../models/schedule-d-form-model';
import { ScheduleDFormService } from '../../services/schedule-d-form/schedule-d-form.service';
import { ScheduleDStepHandlerService } from '../../services/schedule-d-step-handler/schedule-d-step-handler.service';
import { ScheduleDStepOneComponent } from '../schedule-d-step-one/schedule-d-step-one.component';
import { ScheduleDStepThreeComponent } from '../schedule-d-step-three/schedule-d-step-three.component';
import { ScheduleDStepTwoComponent } from '../schedule-d-step-two/schedule-d-step-two.component';
import { ScheduleDHelperMessages } from './constants/schedule-d-helper-messages';

@Component({
    selector: 'app-schedule-d-base',
    imports: [
        AsyncPipe,
        MatSelectModule,
        MatFormFieldModule,
        DialogTooltipComponent,
        HoverTooltipComponent,
        FormsModule,
        MatInputModule,
        MatButtonModule,
        ReactiveFormsModule,
        MatIconModule,
        ScheduleDStepOneComponent,
        ScheduleDStepTwoComponent,
        ScheduleDStepThreeComponent,
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './schedule-d-base.component.html',
})
export class ScheduleDBaseComponent implements OnInit, OnDestroy {
    @Input() scheduleDs!: FormArray;
    @Input() atMaximumFixedLocations: boolean = true;

    @Output() saveScheduleD = new EventEmitter<{
        form: FormGroup<FormGroupType<ScheduleDFormModel>>;
        isSubmitAction: boolean;
    }>();
    @Output() formChange = new EventEmitter<boolean>();

    private readonly stepService = inject(ScheduleDStepHandlerService);
    private readonly scheduleDFormService = inject(ScheduleDFormService);
    private readonly applicationFormControlManager = inject(
        ApplicationFormControlManagerService,
    );

    form: FormGroup<FormGroupType<ScheduleDFormModel>> =
        this.scheduleDFormService.getFormDefinition();

    readonly step = signal(1);

    /*
     * Whether the current step is valid. Used in the template to disable the next button.
     */
    stepValid$: Observable<boolean> = of(false);

    regExPattern = RegExPattern;
    messages = ScheduleDHelperMessages;
    navigationText = NavigationText;

    private destroy$ = new Subject<void>();

    constructor() {}

    loadScheduleD(
        scheduleD: FormGroup<FormGroupType<ScheduleDFormModel>>,
    ): void {
        this.step.set(this.getFirstStep());
        this.form.patchValue(scheduleD.value);
    }

    isReadOnly(): boolean {
        return this.applicationFormControlManager.isReadOnly();
    }

    getFirstStep(): number {
        return this.stepService.getFirstStep();
    }

    getLastStep(): number {
        return this.stepService.getLastStep();
    }

    getStepRange(): number[] {
        return this.stepService.getStepRange();
    }

    previousStep(): void {
        this.saveStep();

        this.step.set(this.getNextNumberForPreviousStepAction());

        this.updateFormValidity();
    }

    nextAction(): void {
        if (this.step() === this.getLastStep()) {
            this.submit();
            return;
        }

        this.saveStep();

        this.step.set(this.getNextNumberForNextStepAction());

        this.updateFormValidity();
    }

    getNextActionText(): string {
        return this.step() === this.getLastStep()
            ? this.navigationText.FINISH
            : this.navigationText.NEXT;
    }

    saveStep(isSubmitAction: boolean = false): void {
        if (this.isReadOnly()) {
            return;
        }

        this.saveScheduleD.emit({
            form: this.form,
            isSubmitAction: isSubmitAction,
        });
    }

    ngOnInit(): void {
        this.setupFormHandlers();
        this.setupForm();
        this.setupReadOnly();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    getNextNumber() {
        const numbers = this.scheduleDs.controls
            .map((control) => control.get('number')?.value)
            .filter((value): value is number => typeof value === 'number');

        return (numbers.length > 0 ? Math.max(...numbers) : 0) + 1;
    }

    submit(): void {
        this.saveStep(true);

        this.form.reset();
        this.setupForm();

        this.step.set(this.getFirstStep());
        this.updateFormValidity();
    }

    /**
     * Gets the next step number adjusted for cases where steps are missing.
     * @returns the next step number for a next action
     */
    private getNextNumberForNextStepAction(): number {
        if (this.step() === 1 && !this.stepService.hasStepTwo(this.form)) {
            return this.step() + 2;
        } else {
            return this.step() + 1;
        }
    }

    /**
     * Gets the previous step number adjusted for cases where steps are missing.
     * @returns the next step number for a previous action
     */
    private getNextNumberForPreviousStepAction(): number {
        if (this.step() === 3 && !this.stepService.hasStepTwo(this.form)) {
            return this.step() - 2;
        } else {
            return this.step() == this.getFirstStep()
                ? this.step()
                : this.step() - 1;
        }
    }

    /**
     * Initializes and enforces read-only behavior for the current Schedule D context.
     */
    private setupReadOnly() {
        if (this.applicationFormControlManager.isReadOnly()) {
            this.applicationFormControlManager.disable(this.form, {
                emitEvent: false,
            });
        }
    }

    /*
     * Sets up the form with default values.
     */
    private setupForm() {
        if (this.form.controls.number.value === null) {
            this.form.controls.number.setValue(this.getNextNumber());
            this.form.controls.actionCode.setValue(ActionCode.Add);
            this.form.controls.tempId.setValue(GridHelper.generateTempRowId());
        }
    }

    private updateFormValidity(): void {
        this.stepService.updateFormControls(this.step(), this.form);
        this.stepValid$ = this.stepService.isStepValid(this.step(), this.form);
    }

    private setupFormHandlers() {
        this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.formChange.emit(this.form.dirty);
            this.updateFormValidity();
        });

        this.form.valueChanges
            .pipe(
                filter(() => this.form.touched && this.form.dirty),
                debounceTime(100),
                takeUntil(this.destroy$),
            )
            .subscribe(() => this.saveStep());
    }
}
